import BaseService from '@/service/base';
import { Config } from '@/utils/service/http';
import {
    stableExerciseEnum,
    openingExerciseEnum,
    randomExerciseEnum,
    sprintExerciseEnum,
    sprintPKEnum,
} from '@/enums/interfaceEnum';

export enum exerciseBoolEnum {
    YES = 1,
    NO = 0,
}

export enum getExerciseType {
    DEFAULT = 1,
    RECONNECT = 2,
}

export type SubjectInfo = {
    id: string;
    title: string;
    pgn: string;
    cateId: string;
};

export class StableExerciseService extends BaseService {
    /**
     * 获取习题类目列表
     */
    static async getSubjectCateList(payload: {
        /**
         * 传 0 是获取难度列表。
         * 不传 或者传 难度id 获取的是该难度下的题目列表
         */
        cateId: number;
    }): Promise<
        Array<{ id: number; title: string; progress: string }>
    > {
        return this.get(stableExerciseEnum.CATE, payload);
    }

    /**
     * 获取习题列表
     * @param payload 包含 cateId
     * @returns
     */
    static async getSubjectList(payload: {
        cateId: number;
    }): Promise<Array<{ id: number; title: string; status: exerciseBoolEnum }>> {
        return this.post(stableExerciseEnum.LIST, payload);
    }

    /**
     * 获取习题详情
     * @param payload 指定题目 id
     */
    static async getSubjectInfo(payload: {
        subjectId: number;
    }): Promise<{ id: number; title: string; pgn: string; cate_id: string }> {
        return this.post(stableExerciseEnum.INFO, payload);
    }

    /**
     * 获取默认习题
     * @returns
     */
    static async getLastSubject() {
        return this.post(stableExerciseEnum.LAST);
    }

    /**
     * 完成习题
     * @param payload 指定题目 id
     */
    static async completeSubject(payload: { subjectId: number; award?: exerciseBoolEnum }) {
        return this.post(stableExerciseEnum.FINISH, payload);
    }

    /**
     * 获取下一题
     * @returns
     */
    static async getNextSubject(payload = {}, callback?: any) {
        return this.post(stableExerciseEnum.NEXT, payload, { error: callback?.error });
    }
}

export class OpeningExerciseService extends BaseService {
    /**
     * 获取习题类目列表
     */
    static async getSubjectCateList(): Promise<
        Array<{ title: string; id: string; progress: string }>
    > {
        return this.post(openingExerciseEnum.CATE);
    }

    /**
     * 获取习题列表
     * @param payload 包含 cateId
     * @returns
     */
    static async getSubjectList(payload: {
        cateId: string;
    }): Promise<Array<{ id: string; title: string; status: exerciseBoolEnum }>> {
        return this.post(openingExerciseEnum.LIST, payload);
    }

    /**
     * 获取习题详情
     * @param payload 指定题目 id
     */
    static async getSubjectInfo(payload: {
        subjectId: string;
    }): Promise<{ id: string; title: string; pgn: string; cate_id: string }> {
        return this.post(openingExerciseEnum.INFO, payload);
    }

    /**
     * 获取默认习题
     * @returns
     */
    static async getLastSubject() {
        return this.post(openingExerciseEnum.LAST);
    }

    /**
     * 完成习题
     * @param payload 指定题目 id
     */
    static async completeSubject(payload: { subjectId: string }) {
        return this.post(openingExerciseEnum.FINISH, payload);
    }

    /**
     * 获取下一题
     * @returns
     */
    static async getNextSubject(payload = {}, callback: any) {
        return this.post(openingExerciseEnum.NEXT, payload, { error: callback?.error });
    }
}

export class RandomExerciseService extends BaseService {
    /**
     * 通知服务器开启本次随机
     * @returns
     */
    static async startSubject() {
        return this.post(randomExerciseEnum.START);
    }

    /**
     * 获取随机题目
     * @returns
     */
    static async getRandSubject(
        payload: any = {},
        config?: Config,
    ): Promise<{ id: string; pgn: string; timer: number }> {
        return this.post(randomExerciseEnum.GET, payload, config);
    }

    /**
     * 完成当前题目
     * @param payload
     * @returns
     */
    static async completeSubject(payload: {
        timer: string;
        subjectId: string;
        isCorrect: exerciseBoolEnum;
    }, config: Config): Promise<{
        timer: string;
        point: string;
        currentPoint: string;
        isCorrect: exerciseBoolEnum;
        accuracy: string;
        badge: Array<{
            icon: string;
            prev: string;
            name: string;
        }>;
    }> {
        return this.post(randomExerciseEnum.FINISH, payload, { timeout: 30 * 1000, ...config });
    }
}

export class SprintExerciseService extends BaseService {
    /**
     * 通知服务器开启本次冲刺
     * @returns
     */
    static async startSprint(): Promise<{ time: number; chessColor: number; goingPoint: number }> {
        // @ts-ignore
        return await this.post(sprintExerciseEnum.START);
    }

    /**
     * 获取习题
     * @returns
     */
    static async getSubjectList(): Promise<
        Record<
            any,
            Array<{
                id: string;
                pgn: string;
                level: string;
            }>
        >
    > {
        return this.post(sprintExerciseEnum.GET);
    }

    static async move(payload: any, config: Config) {
        return this.post(sprintExerciseEnum.MOVE, payload, { timeout: 30 * 1000, ...config });
    }
}

export class SprintPKService extends BaseService {
    /**
     * 初始化房间
     * @returns { time: 游戏时间, chessColor: 棋子颜色（1白0黑）, roomId: 房间唯一标识, master: 房间拥有者 }
     */
    static async initRoom(): Promise<{
        time: number;
        chessColor: 0 | 1;
        roomId: string;
        master: string;
        status: 0 | 1;
        step: number;
        continuitySuccessNum: number;
        awardLevel: number;
        combo: number;
    }> {
        return this.post(sprintPKEnum.INIT);
    }

    /**
     * 邀请好友
     * @returns 无实际意义返回值
     */
    static async inviteUser(payload: { roomId: string; friendUserId: string }, config?: Config) {
        return this.post(sprintPKEnum.INVITE, payload, config);
    }

    /**
     * 批量邀请好友
     * @param payload
     * @param config
     */
    static async inviteBatchUser(
        payload: { roomId: string; friendUserIds: Array<string> },
        config?: Config,
    ) {
        return this.post(sprintPKEnum.INVITE_ALL, payload, config);
    }

    /**
     * 加入房间
     */
    static async joinRoom(payload: { roomId: string }, config?: Config) {
        return this.post(sprintPKEnum.JOIN, payload, config);
    }

    /**
     * 拒绝邀请
     * @param payload
     * @param config
     * @returns
     */
    static async refuseInvite(payload: { roomId: string }, config?: Config) {
        return this.post(sprintPKEnum.REFUSE, payload, config);
    }

    /**
     * 退出房间
     * @param payload
     * @param config
     */
    static async exitRoom(payload: { roomId: string }, config?: Config) {
        return this.post(sprintPKEnum.EXIT, payload, config);
    }

    /**
     * 开始游戏
     * @param payload
     * @param config
     * @returns
     */
    static async startGame(payload: { roomId: string }, config?: Config) {
        return this.post(sprintPKEnum.START, payload, config);
    }

    /**
     * 获取房间内用户列表
     * @param payload
     * @param config
     * @returns
     */
    static async getUserList(payload: { roomId: string }, config?: Config) {
        return this.post(sprintPKEnum.USER, payload, config);
    }

    /**
     * 获取习题列表
     * @param payload
     * @param config
     */
    static async getSubjectList(
        payload: { roomId: string; type: getExerciseType },
        config?: Config,
    ): Promise<Array<{ id: string; pgn: string }>> {
        return this.post(sprintPKEnum.GET, payload, config);
    }

    static async move(payload: any) {
        return this.post(sprintPKEnum.MOVE, payload);
    }
}
